import React from 'react';
import { configureAnchors } from 'react-scrollable-anchor';

import { getFluidGatsbyImage } from '../../utils/gatsby-storyblok-image-fixed';
import RelatedProducts from '../related-products';
import {
  getProductDetailsDownloads,
  getProductDetailsFaq,
  getProductDetailsSizes,
  getProductDetailsTechSpecs,
  getProductDetailsVideos
} from '../../utils/product';
import { ProductFAQ } from '../product/faq';
import Intro4 from '../intro4';
import parseStoryblokLink from '../../utils/parse-storyblok-link';
import RailsSection from '../rails-section';
import InfoImageCard from '../info-image-card';
import { getPath } from '../../utils/paths';
import { VideosSection } from '../section/videos';
import { ProductDownloadsSection } from '../product/downloads-section';
import SectionCta from '../section-cta';
import Container from '../container';
import { ProductSizeGuide } from '../product/size-guide';
import { ProductTechSpecs } from '../product/tech-specs';
import { ProductDescription } from '../product/description';
import { ProductOverview } from '../product/overview';
import { ProductHero } from '../product/hero';
import PageSubnavBar from '../page-subnav-bar';
import { DividedContent } from '../product/divider';
import { getLocation } from '../../utils/window';
import CaseStudiesSlider from '../case-studies-slider/CaseStudiesSlider';
import StoryblokCtaBanner from './adapters/storyblok-cta-banner-adapter';

export interface StoryblokProductComponentProps {
  productFeatureIcons?: any[];
  productFeatures?: any[];
  productDetails: {
    hideTechSpecs?: boolean;
    hideShapesAndSizes?: boolean;
    techSpecs?: {
      content?: unknown;
    }[];
    shapesAndSizes?: {
      largeDataMode?: boolean;
    } & {
      sizeGuideFile?: {
        filename: string;
      };
    }[];
    hideVideos?: boolean;
    videos?: {
      _uid: string;
      videoThumbnail: { filename: string };
      videoTitle: string;
      videoType: string;
      videoLength: string;
      videoSource?: { filename: string };
      videoLink?: string;
    }[];
    hideDownloads?: boolean;
    downloads?: Record<any, any>[];
    hideFAQs?: boolean;
    faqs?: {
      _uid: string;
      component: string;
      question: string;
      answer: { [key: string]: any };
    }[];
  }[];
  featuredCaseStudiesTitle?: string;
  featuredCaseStudiesDescription?: string;
  featuredCaseStudies?: {
    _uid: string;
    title: string;
    subtitle: string;
    caseStudies: {
      full_slug: string;
      content: {
        title: string;
        gridImage?: Record<any, any>;
        featuredImage?: Record<any, any>;
        gallery: Record<any, any>[];
        about: {
          projectStats: {
            name: string;
            value: string;
          }[];
        }[];
      };
    }[];
  }[];
  relatedProducts: {
    full_slug: string;
    content: {
      productName: string;
      productThumbnail: Record<any, any>;
    };
  }[];
  productName: string;
  headerDescription?: string;
  description?: Record<any, any>;
  datasheetFile?: { filename: string };
  faq?: {
    _uid: string;
    name: string;
    content: {
      items: {
        _uid: string;
        title: string;
        text: { [key: string]: unknown };
      }[];
    }[];
  }[];
  systemTitle?: string;
  systemDescription?: string;
  systems?: {
    _uid: string;
    title?: string;
    paragraph?: Record<string, any>;
    icon?: Record<string, any>;
    ctaText?: string;
    ctaLink?: Record<string, any>;
  }[];
  systemTypes?: {
    _uid: string;
    title: string;
    image: { filename: string };
  }[];
  leadTimesTitle?: string;
  leadTimesDescription?: string;
  leadTimesDownload?: Record<string, any>;
  leadTimesDownloadText?: string;
  leadTimesStock?: string;
  leadTimesOrder?: string;
  deliveryMessage?: string;
  customisationFeature?: string;
  customisationModalLabel?: string;
  customisationModalTitle?: string;
  customisationModalImage?: Record<any, any>;
  customisationModalContent?: any;
  gallery?: {
    _uid: number;
    image: {
      filename: string;
    };
    video?: {
      filename: string;
    };
    videoUrl?: string;
  }[];
  ceIcon?: boolean;
  ukcaIcon?: boolean;
  caseStudiesSlider?: any[];
  footerCTA?: {
    content?: Record<any, any>;
  };
  compareLink?: {
    title: string;
    link: Record<string, any>;
  }[];
}

/**
 * Workaround so the section title shows.
 */
configureAnchors({ offset: -160 });

function StoryblokProductComponent({
  productFeatureIcons,
  productFeatures,
  featuredCaseStudiesTitle,
  featuredCaseStudiesDescription,
  featuredCaseStudies,
  relatedProducts,
  productName,
  headerDescription,
  description,
  datasheetFile,
  systemTitle,
  systemDescription,
  systems,
  systemTypes,
  leadTimesTitle,
  leadTimesDescription,
  leadTimesDownload,
  leadTimesDownloadText,
  leadTimesStock,
  leadTimesOrder,
  deliveryMessage,
  customisationFeature,
  customisationModalLabel,
  customisationModalTitle,
  customisationModalImage,
  customisationModalContent,
  gallery,
  ceIcon,
  ukcaIcon,
  caseStudiesSlider,
  footerCTA,
  compareLink,
  ...props
}: StoryblokProductComponentProps) {
  const productDetails = props.productDetails[0];
  const techSpecs = getProductDetailsTechSpecs(productDetails);
  const sizesGuide = getProductDetailsSizes(productDetails);
  const downloads = getProductDetailsDownloads(productDetails);
  const videos = getProductDetailsVideos(productDetails);
  const faq = props.faq?.length !== 0 ? props.faq : undefined;
  const legacyFaq = getProductDetailsFaq(productDetails);
  const location = getLocation();

  const relatedProductsParsed = relatedProducts.map((product) => {
    const productImg = getFluidGatsbyImage(product.content.productThumbnail, {
      maxWidth: 500
    });
    return {
      name: product.content.productName,
      link: '/' + product.full_slug,
      image: productImg
    };
  });

  return (
    <React.Fragment>
      <PageSubnavBar
        title="Products"
        subtitle={productName}
        swapTitleStyling
        pages={[
          {
            title: 'Overview',
            link: '#overview',
            active: location?.hash === '#overview'
          },
          techSpecs && {
            title: 'Tech Specs',
            link: '#tech-specs',
            active: location?.hash === '#tech-specs'
          },
          sizesGuide && {
            title: 'Size Guide',
            link: '#size-guide',
            active: location?.hash === '#size-guide'
          },
          downloads && {
            title: 'Downloads',
            link: '#downloads',
            active: location?.hash === '#downloads'
          },
          videos && {
            title: 'Videos',
            link: '#videos',
            active: location?.hash === '#videos'
          },
          (faq || legacyFaq) && {
            title: 'FAQ',
            link: '#faq',
            active: location?.hash === '#faq'
          },
          {
            title: 'Get a Quote',
            link: getPath.contact(),
            primary: true
          }
        ].filter((al) => !!al)}
      />

      <ProductHero
        title={productName}
        text={headerDescription}
        showSizes={!!sizesGuide}
        dataSheet={datasheetFile?.filename}
        systems={systemTypes}
        customisationFeature={customisationFeature}
        customisationModal={{
          label: customisationModalLabel,
          title: customisationModalTitle,
          image: customisationModalImage,
          content: customisationModalContent
        }}
        leadTimesStock={leadTimesStock}
        leadTimesOrder={leadTimesOrder}
        deliveryMessage={deliveryMessage}
        gallery={gallery}
        ceIcon={ceIcon}
        ukcaIcon={ukcaIcon}
      />

      <DividedContent>
        <ProductOverview
          title="At a Glance"
          highlights={productFeatureIcons}
          features={productFeatures}
          compareLink={compareLink?.[0]}
        />

        <ProductDescription text={description} />

        <ProductTechSpecs items={techSpecs} />

        <>
          <ProductSizeGuide items={sizesGuide} />

          {leadTimesTitle && leadTimesDownload?.filename && (
            <Container large>
              <SectionCta
                title={leadTimesTitle}
                subtitle={leadTimesDescription}
                ctaText={leadTimesDownloadText}
                ctaLink={leadTimesDownload.filename}
                addBorderRadius
                secondLink={{
                  to: getPath.contact(),
                  title: 'Get a Quote'
                }}
                icon="lead"
                slim
              />
            </Container>
          )}

          {downloads && <ProductDownloadsSection downloads={downloads} />}
        </>

        {videos && (
          <VideosSection
            videos={videos.map((video) => ({
              id: video._uid,
              name: video.videoTitle,
              image: video.videoThumbnail,
              source: video.videoLink || video.videoSource?.filename,
              duration: Number.parseInt(video.videoLength, 10),
              categories: video.videoType || 'Product Video'
            }))}
          />
        )}

        <ProductFAQ anchor="faq" faq={faq} legacyFaq={legacyFaq} />
      </DividedContent>

      {caseStudiesSlider &&
        caseStudiesSlider?.length > 0 &&
        caseStudiesSlider?.map((caseStudy) => (
          <CaseStudiesSlider
            sectionCtaLink={parseStoryblokLink(caseStudy.sectionCtaLink)}
            {...caseStudy}
            largerContainer
            key={caseStudy._uid}
          />
        ))}

      {caseStudiesSlider && caseStudiesSlider?.length !== 0
        ? null
        : featuredCaseStudies?.[0] && (
            <RailsSection
              title={featuredCaseStudiesTitle || 'Related Case Studies'}
              text={
                featuredCaseStudiesDescription ||
                'Explore real projects using our smoke ventilation products across both residential and commercial sectors.'
              }
              cta={{
                to: getPath.caseStudies(),
                title: 'View all case studies'
              }}
              dark
            >
              {featuredCaseStudies.map((caseStudy) => (
                <InfoImageCard
                  key={caseStudy._uid}
                  title={caseStudy.title}
                  text={caseStudy.subtitle}
                  link={{
                    title: 'View Case Study',
                    to: `/${caseStudy.caseStudies[0]?.full_slug}`
                  }}
                  image={getFluidGatsbyImage(
                    caseStudy.caseStudies[0]?.content.featuredImage
                  )}
                  keyPoints={caseStudy.caseStudies[0]?.content.about[0]?.projectStats.map(
                    (projectStat) => ({
                      title: projectStat.name,
                      text: projectStat.value
                    })
                  )}
                />
              ))}
            </RailsSection>
          )}

      {systems && systems.length > 0 && (
        <Intro4
          title={systemTitle || 'Systems'}
          titleProps={{ as: 'h2', fontSizeMedium: true }}
          text={
            systemDescription ||
            'These are the key systems used - choose a solution to find out more.'
          }
          links={systems.map((system) => ({
            id: system._uid,
            name: system.title,
            text: system.paragraph,
            icon: {
              aspectRatio: 1,
              src: system.icon?.filename
            },
            link: parseStoryblokLink(system.ctaLink),
            linkText: system.ctaText
          }))}
          backgroundColor="lightGrey"
          centerItems
        />
      )}

      <RelatedProducts
        title="Explore more products in our range"
        products={relatedProductsParsed}
      />

      {footerCTA?.content && <StoryblokCtaBanner {...footerCTA.content} />}
    </React.Fragment>
  );
}

export default StoryblokProductComponent;
